import { AxiosError } from 'axios'
import CustomAlert from 'components/custom-alert'
import { PropsWithChildren, createContext, useContext, useState } from 'react'

type Severity = 'success' | 'error' | 'warning' | 'info'

interface IAlert {
  message: string
  severity: Severity
  isOpen: boolean
}

interface IAlertContextData {
  openAlert: (message: string, severity: Severity, error?: AxiosError) => void
  closeAlert: () => void
}

const AlertContext = createContext<IAlertContextData>({} as IAlertContextData)

const getMessage = (error: AxiosError, defaultMessage: string) => {
  const status = error.response?.status

  if (status === 400) {
    return (error.response?.data as any)?.data?.message || defaultMessage
  }

  return defaultMessage
}

const AlertProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [openAlert, setOpenAlert] = useState<IAlert>({
    message: '',
    severity: 'success',
    isOpen: false,
  })

  const handleCloseAlert = () => {
    setOpenAlert((prev) => ({ ...prev, isOpen: false }))
  }

  const handleOpenAlert = (message: string, severity: Severity, error?: AxiosError) => {
    if (error) {
      message = getMessage(error, message)
    }

    setOpenAlert({
      message,
      severity,
      isOpen: true,
    })
  }

  return (
    <AlertContext.Provider
      value={{
        openAlert: handleOpenAlert,
        closeAlert: handleCloseAlert,
      }}
    >
      <CustomAlert
        open={openAlert.isOpen}
        setOpen={handleCloseAlert}
        severity={openAlert.severity}
        message={openAlert.message}
      />
      {children}
    </AlertContext.Provider>
  )
}

const useAlert = () => useContext(AlertContext)

export { AlertProvider, useAlert }
